"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { SERVICE_QUEUE_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetSubjects from "@/hooks/requests/subject/use-get-subjects";
import useHasNoPermission from "@/hooks/use-check-permissions";
import usePagination from "@/hooks/use-pagination";
import { SubjectMessages } from "@/messages/subject/subject.messages";

import { SubjectTable } from "./components/Table";

type Props = {
  messages: SubjectMessages;
};

const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;

function SubjectPage({ messages }: Props) {
  const pagination = usePagination();
  const router = useNavigate();
  const { pageNumber, pageSize } = pagination;
  const { lang } = useLanguage();

  const { data, isLoading, error } = useGetSubjects({ pageNumber, pageSize });
  const hasNoPermissionToCreate = useHasNoPermission(SERVICE_QUEUE_PERMISSIONS.CREATE);

  const shouldLoadTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoFound = !!data && !isLoading && !error && data.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.subjectTableTitle}
                </MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createSubject(lang))}
                  disabled={hasNoPermissionToCreate}
                >
                  {messages.addSubjectButton}
                </MDButton>
              </MDBox>

              {!!shouldLoadTable && (
                <SubjectTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.subjectErrorToLoad}
                  description={error.message}
                  alt={messages.subjectNotFoundAlt}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.subjectNotFoundError}
                  description={messages.subjectNotFoundDescription}
                  alt={messages.subjectNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default SubjectPage;
